import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import FloatingNav from '../components/FloatingNav';
import { 
  Camera, 
  History, 
  User, 
  LogOut, 
  Crown, 
  ChevronRight, 
  Sparkles,
  Footprints,
  TrendingUp,
  Calendar,
  Settings,
  Bell,
  CheckCircle,
  Upload,
  FileText,
  X,
  Zap,
  Star,
  Scan,
  Cpu,
  Activity
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_661e4868-d868-458c-86d6-2d10e12396a2/artifacts/tx0slj8p_ChatGPT_Image_15_de_mai._de_2026__13_56_39-removebg-preview.png";

const PLANS = [
  { id: 'pack5', name: 'Essencial', price: 19.90 },
  { id: 'detailed10', name: 'Plus', price: 49.90 },
  { id: 'advanced', name: 'Premium', price: 99.00 }
];

const Dashboard = () => {
  const { user, subscription, readingsRemaining, logout, api, refreshSubscription } = useAuth();
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReadings();
    refreshSubscription();
    
    // Check for payment success redirect
    const payment = searchParams.get('payment');
    const paymentId = searchParams.get('payment_id');
    const status = searchParams.get('status');
    const externalRef = searchParams.get('external_reference');
    
    if (payment === 'success' || status === 'approved') {
      verifyPayment(paymentId, status, externalRef);
    }
  }, []);

  const verifyPayment = async (paymentId, status, externalRef, retryCount = 0) => {
    try {
      const response = await api.get(`/subscription/verify-payment?payment_id=${paymentId || ''}&status=${status || ''}&external_reference=${externalRef || ''}`);
      
      if (response.data.success) {
        setPaymentSuccess(true);
        await refreshSubscription();
        // Clear URL params after 3 seconds
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
          setPaymentSuccess(false);
        }, 5000);
      } else if (response.data.status === 'pending' && retryCount < 5) {
        // Retry after 3 seconds if pending
        setTimeout(() => {
          verifyPayment(paymentId, status, externalRef, retryCount + 1);
        }, 3000);
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
    }
  };

  const handleUploadReceipt = async () => {
    if (!selectedPlan || !receiptFile) {
      alert('Selecione um plano e o comprovante');
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('plan_id', selectedPlan);
      formData.append('receipt', receiptFile);
      
      const response = await api.post('/subscription/upload-receipt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        alert('Comprovante enviado com sucesso! Aguarde a verificação pelo administrador.');
        setShowReceiptModal(false);
        setSelectedPlan('');
        setReceiptFile(null);
      }
    } catch (error) {
      console.error('Error uploading receipt:', error);
      alert('Erro ao enviar comprovante. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const fetchReadings = async () => {
    try {
      const response = await api.get('/readings');
      setReadings(response.data.readings.slice(0, 5));
    } catch (error) {
      console.error('Error fetching readings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressPercent = () => {
    if (!subscription) {
      return user?.free_reading_used ? 100 : 0;
    }
    const plans = { pack5: 5, detailed10: 10, advanced: 20 };
    const total = plans[subscription.plan_id] || 5;
    const used = total - readingsRemaining;
    return (used / total) * 100;
  };

  const getReadingsText = () => {
    if (!subscription) {
      if (user?.free_reading_used) {
        return 'Leitura gratuita utilizada';
      }
      return '1 leitura gratuita disponível';
    }
    return `${readingsRemaining} leituras restantes este mês`;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const canMakeReading = () => {
    if (subscription) {
      return readingsRemaining > 0;
    }
    return !user?.free_reading_used;
  };

  return (
    <div className="min-h-screen pb-28" style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
      {/* Payment Success Toast */}
      {paymentSuccess && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up">
          <div className="bg-emerald-500 text-white px-6 py-4 rounded-2xl shadow-xl flex items-center gap-3 glow-success">
            <CheckCircle className="h-6 w-6" />
            <div>
              <p className="font-bold">Pagamento confirmado!</p>
              <p className="text-sm text-white/90">Sua assinatura foi ativada com sucesso.</p>
            </div>
          </div>
        </div>
      )}

      {/* Header — Tech Hero */}
      <header
        className="px-6 pt-6 pb-24 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at top right, #0F766E 0%, #083D3A 60%, #0A0F14 100%)',
        }}
      >
        {/* Scanner grid */}
        <div className="absolute inset-0 scanner-grid opacity-30 pointer-events-none" />
        {/* Neon orbs */}
        <div className="absolute -top-20 -right-10 w-64 h-64 rounded-full"
             style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.35) 0%, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="absolute bottom-0 -left-10 w-56 h-56 rounded-full"
             style={{ background: 'radial-gradient(circle, rgba(212,162,86,0.25) 0%, transparent 70%)', filter: 'blur(40px)' }} />

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <img src={LOGO_URL} alt="AJ HealthMap" className="h-28 md:h-32 w-auto drop-shadow-lg" data-testid="dashboard-logo" />
            <div className="flex items-center gap-2">
              <button className="p-2 text-white/70 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5">
                <Bell className="h-5 w-5" />
              </button>
              <button 
                onClick={handleLogout}
                className="p-2 text-white/70 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          <div className="animate-fade-in-up">
            <div className="hud-chip mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              Painel Ativo
            </div>
            <p className="text-white/60 text-sm mb-1">Olá,</p>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="user-greeting">
              {user?.name?.split(' ')[0] || 'Usuário'}
            </h1>
          </div>
        </div>
      </header>

      {/* Main content - overlapping header */}
      <main className="px-4 -mt-16 relative z-20 max-w-lg mx-auto">
        {/* Credits Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-6 mb-6 animate-fade-in-up relative overflow-hidden" 
             data-testid="credits-card"
             style={{ border: '1px solid rgba(15,118,110,0.15)', boxShadow: '0 25px 60px rgba(8,61,58,0.18)' }}>
          {/* Top neon line */}
          <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #22D3EE, #D4A256, transparent)' }} />
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
                   style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', boxShadow: '0 6px 20px rgba(212,162,86,0.35)' }}>
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-[#0A0F14]" style={{ fontFamily: 'Syne, sans-serif' }}>Suas Leituras</h3>
                <p className="text-sm text-[#6B7280]" data-testid="readings-remaining">{getReadingsText()}</p>
              </div>
            </div>
            {subscription && (
              <span className="text-xs px-3 py-1 rounded-full font-bold"
                    style={{ background: 'rgba(34,211,238,0.1)', color: '#0F766E', border: '1px solid rgba(34,211,238,0.3)' }}>
                {subscription.plan_id === 'pack5' && 'Essencial'}
                {subscription.plan_id === 'detailed10' && 'Plus'}
                {subscription.plan_id === 'advanced' && 'Premium'}
              </span>
            )}
          </div>
          
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#6B7280]">Progresso</span>
              <span className="font-bold text-[#0F766E]">{Math.round(getProgressPercent())}%</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(15,118,110,0.08)' }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: 'linear-gradient(90deg, #0F766E, #22D3EE)' }}
                initial={{ width: 0 }}
                animate={{ width: `${getProgressPercent()}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
          </div>

          {!subscription && user?.free_reading_used && (
            <Link to="/planos">
              <Button className="w-full text-white rounded-full h-12 font-semibold transition-all hover:shadow-xl" 
                      style={{ background: 'linear-gradient(135deg, #D4A256, #E8BC6A)', boxShadow: '0 6px 20px rgba(212,162,86,0.4)' }}
                      data-testid="upgrade-btn">
                <Crown className="h-5 w-5 mr-2" />
                Desbloquear Premium
              </Button>
            </Link>
          )}
          
          {/* Botão de enviar comprovante */}
          {!subscription && (
            <Button 
              onClick={() => setShowReceiptModal(true)}
              variant="outline"
              className="w-full mt-3 rounded-full h-11"
              style={{ border: '2px solid #0F766E', color: '#0F766E', background: 'transparent' }}
              data-testid="upload-receipt-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Enviar Comprovante de Pagamento
            </Button>
          )}
        </div>

        {/* New Reading CTA — Scanner Button */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <button
            onClick={() => canMakeReading() && navigate('/nova-leitura')}
            disabled={!canMakeReading()}
            data-testid="new-reading-btn"
            className={`w-full relative overflow-hidden rounded-2xl p-5 flex items-center gap-4 transition-all ${
              canMakeReading() ? 'hover:shadow-2xl hover:-translate-y-0.5' : 'opacity-60 cursor-not-allowed'
            }`}
            style={{
              background: 'linear-gradient(135deg, #083D3A 0%, #0F766E 50%, #14B8A6 100%)',
              boxShadow: '0 12px 40px rgba(15,118,110,0.4)',
            }}
          >
            {/* Animated scan line */}
            {canMakeReading() && (
              <motion.div
                className="absolute inset-y-0 w-1 opacity-50"
                style={{ background: 'linear-gradient(180deg, transparent, #22D3EE, transparent)', boxShadow: '0 0 20px #22D3EE' }}
                animate={{ left: ['-5%', '105%'] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
              />
            )}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
                 style={{ background: 'rgba(34,211,238,0.18)', border: '1px solid rgba(34,211,238,0.35)' }}>
              <Scan className="h-7 w-7 text-cyan-300" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-[10px] font-bold tracking-[0.2em] text-cyan-300 uppercase">Nova Leitura</p>
              <p className="text-white font-bold text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>Iniciar Scanner de IA</p>
            </div>
            <ChevronRight className="h-6 w-6 text-white/70" />
          </button>
          {!canMakeReading() && (
            <p className="text-center text-sm text-[#6B7280] mt-3">
              Assine um plano para continuar suas leituras
            </p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-3 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <Link 
            to="/planos" 
            className="bg-white rounded-2xl p-4 text-center transition-all hover:shadow-xl hover:-translate-y-0.5 relative overflow-hidden"
            style={{ border: '1px solid rgba(212,162,86,0.2)' }}
            data-testid="plans-link"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
                 style={{ background: 'rgba(212,162,86,0.12)' }}>
              <Crown className="h-6 w-6" style={{ color: '#D4A256' }} />
            </div>
            <p className="text-sm font-bold text-[#0A0F14]">Planos</p>
          </Link>
          <Link 
            to="/historico" 
            className="bg-white rounded-2xl p-4 text-center transition-all hover:shadow-xl hover:-translate-y-0.5"
            style={{ border: '1px solid rgba(34,211,238,0.2)' }}
            data-testid="history-link"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
                 style={{ background: 'rgba(34,211,238,0.12)' }}>
              <History className="h-6 w-6" style={{ color: '#0F766E' }} />
            </div>
            <p className="text-sm font-bold text-[#0A0F14]">Histórico</p>
          </Link>
          <Link 
            to="/conta" 
            className="bg-white rounded-2xl p-4 text-center transition-all hover:shadow-xl hover:-translate-y-0.5"
            style={{ border: '1px solid rgba(15,118,110,0.15)' }}
            data-testid="account-link"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
                 style={{ background: 'rgba(15,118,110,0.08)' }}>
              <User className="h-6 w-6" style={{ color: '#083D3A' }} />
            </div>
            <p className="text-sm font-bold text-[#0A0F14]">Conta</p>
          </Link>
        </div>

        {/* Recent Readings */}
        <section className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg text-[#0A0F14] flex items-center gap-2" style={{ fontFamily: 'Syne, sans-serif' }}>
              <Activity className="w-5 h-5 text-cyan-500" />
              Histórico Recente
            </h2>
            <Link to="/historico" className="text-sm font-bold flex items-center gap-1" style={{ color: '#0F766E' }} data-testid="view-all-link">
              Ver todos
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-4 animate-pulse" style={{ border: '1px solid rgba(0,0,0,0.05)' }}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#E8E4D9]" />
                    <div className="flex-1">
                      <div className="h-4 bg-[#E8E4D9] rounded w-3/4 mb-2" />
                      <div className="h-3 bg-[#E8E4D9] rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : readings.length > 0 ? (
            <div className="space-y-3">
              {readings.map((reading, index) => (
                <Link 
                  key={reading.id} 
                  to={`/leitura/${reading.id}`}
                  className="bg-white rounded-2xl p-4 flex items-center gap-4 transition-all hover:shadow-lg hover:-translate-y-0.5 block relative overflow-hidden"
                  style={{ border: '1px solid rgba(15,118,110,0.1)' }}
                  data-testid={`reading-item-${reading.id}`}
                  // eslint-disable-next-line react/no-unknown-property
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center"
                       style={{ background: 'linear-gradient(135deg, #083D3A, #0F766E)' }}>
                    <Footprints className="h-7 w-7 text-cyan-300" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#0A0F14] mb-1">
                      Leitura de {formatDate(reading.created_at)}
                    </p>
                    <div className="flex items-center gap-3 text-sm">
                      <span className="text-[#6B7280]">
                        {reading.zones?.length || 0} zonas
                      </span>
                      <span className={`font-bold ${
                        reading.reactivity_score >= 0.7 ? 'text-red-500' :
                        reading.reactivity_score >= 0.4 ? 'text-amber-500' : 'text-emerald-500'
                      }`}>
                        {Math.round(reading.reactivity_score * 100)}% reativo
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-[#9CA3AF]" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 text-center" style={{ border: '1px solid rgba(15,118,110,0.1)' }}>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                   style={{ background: 'rgba(34,211,238,0.08)' }}>
                <Scan className="h-8 w-8 text-cyan-500" />
              </div>
              <h3 className="font-bold text-[#0A0F14] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                Nenhuma leitura ainda
              </h3>
              <p className="text-[#6B7280] mb-4">Faça sua primeira leitura reflexológica</p>
              <Button 
                onClick={() => navigate('/nova-leitura')}
                className="text-white rounded-full px-6"
                style={{ background: 'linear-gradient(135deg, #083D3A, #0F766E)' }}
                disabled={!canMakeReading()}
                data-testid="first-reading-btn"
              >
                Iniciar Scanner
              </Button>
            </div>
          )}
        </section>
      </main>

      {/* Floating Navigation */}
      <FloatingNav disabledScan={!canMakeReading()} />

      {/* Modal de envio de comprovante */}
      {showReceiptModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                Enviar Comprovante
              </h3>
              <button 
                onClick={() => setShowReceiptModal(false)}
                className="p-2 hover:bg-[#E8E4D9] rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-[#8E8E93]" />
              </button>
            </div>

            <p className="text-[#8E8E93] mb-6">
              Já fez o pagamento? Envie o comprovante para verificação e liberação do seu plano.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1C1C1E] mb-2">
                  Plano Contratado
                </label>
                <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                  <SelectTrigger className="w-full h-12 rounded-xl border-[#E8E4D9]">
                    <SelectValue placeholder="Selecione o plano" />
                  </SelectTrigger>
                  <SelectContent>
                    {PLANS.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - R$ {plan.price.toFixed(2).replace('.', ',')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1C1C1E] mb-2">
                  Comprovante de Pagamento
                </label>
                <div className="border-2 border-dashed border-[#E8E4D9] rounded-xl p-6 text-center hover:border-[#2E5C55] transition-colors">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="hidden"
                    id="receipt-input"
                  />
                  <label htmlFor="receipt-input" className="cursor-pointer">
                    {receiptFile ? (
                      <div className="flex items-center justify-center gap-2 text-[#2E5C55]">
                        <FileText className="h-6 w-6" />
                        <span className="font-medium">{receiptFile.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-[#8E8E93] mx-auto mb-2" />
                        <p className="text-[#8E8E93]">Clique para selecionar</p>
                        <p className="text-xs text-[#8E8E93] mt-1">JPG, PNG, WebP ou PDF</p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowReceiptModal(false)}
                variant="outline"
                className="flex-1 h-12 rounded-full border-[#E8E4D9]"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUploadReceipt}
                disabled={!selectedPlan || !receiptFile || uploading}
                className="flex-1 h-12 rounded-full bg-gradient-to-r from-[#2E5C55] to-[#3A7068] hover:from-[#234744] hover:to-[#2E5C55] text-white"
              >
                {uploading ? 'Enviando...' : 'Enviar Comprovante'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
